import React, { useState } from 'react';

import classNames from 'classnames';

import { formatActivityDateTime } from '../activity/userActivityFormat';

import TextButton from 'CommonComponents/TextButton';

const VISIBLE_ITEMS = 2;

// The shared table draws a divider between every cell and fills rows grey;
// this table keeps only the row dividers on white rows, as designed.
export const CELL = 'tw-text-start tw-align-middle !tw-border-r-0';
// The design's cells: lighter and a step smaller than the shared table's, with more room.
export const TABLE =
    'inbud-table [&_th]:!tw-px-3 [&_th]:!tw-py-2.5 [&_th]:!tw-text-0.5sm [&_th]:!tw-font-medium [&_th]:!tw-whitespace-nowrap [&_th:first-child]:tw-rounded-s-lg [&_th:last-child]:tw-rounded-e-lg [&_td]:!tw-px-3 [&_td]:!tw-py-2.5 [&_td]:!tw-text-0.5sm [&_td]:!tw-font-light [&_td:first-child]:!tw-font-normal [&_td]:![overflow-wrap:normal]';
export const ROW = '!tw-bg-white';

const PILL = 'tw-inline-flex tw-items-center tw-whitespace-nowrap tw-rounded-full tw-px-[9px] tw-py-0.5 tw-text-xs tw-font-medium';
const COUNT_CHIP =
    'tw-ms-1 tw-inline-flex tw-items-center tw-whitespace-nowrap tw-rounded-full tw-bg-blue-15 tw-px-2 tw-py-px tw-text-[11px] tw-font-medium tw-leading-[14px] tw-text-blue hover:tw-bg-blue-25';

function Muted({ children }) {
    return <span className="tw-text-charcoal-50">{children}</span>;
}

function RolePill({ role, isEventAdmin }) {
    if (!role) {
        return <Muted>{$t('No role')}</Muted>;
    }

    return <span className={classNames(PILL, isEventAdmin ? 'tw-bg-blue-15 tw-text-blue' : 'tw-bg-charcoal-15 tw-text-charcoal-75')}>{$t(role.name)}</span>;
}

/**
 * The first two names plus a "+N" chip that expands the full list in place.
 */
export function CollapsibleList({ items, empty }) {
    const [expanded, setExpanded] = useState(false);

    if (items.length === 0) {
        return <Muted>{empty}</Muted>;
    }

    const hidden = items.length - VISIBLE_ITEMS;

    if (hidden <= 0) {
        return <span>{items.map((item) => item.name).join(', ')}</span>;
    }

    const chip = (
        <button
            type="button"
            className={COUNT_CHIP}
            aria-expanded={expanded}
            aria-label={expanded ? $t('Show less') : $t('Show {{count}} more', { count: hidden })}
            onClick={() => setExpanded(!expanded)}
        >
            {expanded ? $t('show less') : <span dir="ltr">{`+${hidden}`}</span>}
        </button>
    );

    // Expanded, the whole list reads as a smaller secondary block in the cell.
    if (expanded) {
        return (
            <span className="tw-block tw-text-xs tw-leading-normal tw-text-charcoal-75">
                {items.map((item) => item.name).join(', ')} {chip}
            </span>
        );
    }

    return (
        <span>
            {items
                .slice(0, VISIBLE_ITEMS)
                .map((item) => item.name)
                .join(', ')}
            {chip}
        </span>
    );
}

// A member holding cost centres but no department still has budget access, so the cell says so rather than "None".
function costcentersOnly(user) {
    const count = user.costcenters?.length ?? 0;

    if (count === 0) {
        return $t('None');
    }

    return count === 1 ? $t('1 cost centre') : $t('{{count}} cost centres', { count });
}

export default function ProjectUserRow({ user, canEdit, canDelete, onEdit, onRemove }) {
    const admin = user.is_event_admin;

    return (
        <tr className={ROW}>
            <td className={classNames(CELL, '!tw-font-normal')}>
                {user.name}
                {!user.is_active && <span className={classNames(PILL, 'tw-ms-2 tw-bg-charcoal-15 tw-font-normal tw-text-charcoal-75')}>{$t('Disabled')}</span>}
            </td>
            <td className={CELL}>{user.email}</td>
            <td className={CELL}>
                <RolePill role={user.role} isEventAdmin={admin} />
            </td>
            <td className={CELL}>{admin ? <Muted>{$t('All')}</Muted> : <CollapsibleList items={user.departments} empty={costcentersOnly(user)} />}</td>
            <td className={CELL}>{admin ? <Muted>{$t('All')}</Muted> : <CollapsibleList items={user.venues} empty={$t('None')} />}</td>
            <td className={CELL}>{admin ? <Muted>{$t('All modules')}</Muted> : <CollapsibleList items={user.modules} empty={$t('None')} />}</td>
            <td className={CELL}>{user.last_login_at ? <bdi>{formatActivityDateTime(user.last_login_at)}</bdi> : <Muted>{$t('Never')}</Muted>}</td>
            <td className={CELL}>
                <div className="tw-flex tw-flex-col tw-items-start tw-gap-y-0.5 [&_.txt-button]:!tw-text-0.5sm [&_.txt-button]:!tw-font-medium">
                    {canEdit && (
                        <TextButton variant="primary" className="!tw-normal-case" onClick={() => onEdit(user)}>
                            {$t('Edit')}
                        </TextButton>
                    )}
                    {canDelete && (
                        <TextButton variant="danger" className="!tw-normal-case" onClick={() => onRemove(user)}>
                            {$t('Remove')}
                        </TextButton>
                    )}
                </div>
            </td>
        </tr>
    );
}
